<template>
  <div class="uptime-chart-container">
    <canvas ref="chartCanvas" @click="handleCanvasClick"></canvas>
  </div>
  <div id="uptime-chart-tooltip" class="uptime-chart-tooltip" style="display:none;">
    <button v-if="showDownloadButton" class="tooltip-download" @click="downloadOfflineDevices" title="Download offline devices list">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
      </svg>
    </button>
    <button class="tooltip-close" @click="closeTooltip">×</button>
    <div id="uptime-chart-tooltip-content"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  type ChartOptions
} from 'chart.js';
import useApi from '@/helpers/api/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  BarController
);

interface UptimeData {
  playerId: number;
  playerName: string;
  dateStarted: number;
  dateEnded: number;
  uptimeSeconds: number;
  status: 'online' | 'offline';
}

interface Props {
  uptimeData: UptimeData[];
  startDate: Date;
  endDate: Date;
}

const props = defineProps<Props>();

const chartCanvas = ref<HTMLCanvasElement>();
let chart: ChartJS | null = null;
const tooltipPinned = ref(false);
const showDownloadButton = ref(false);
let tooltipPosition = { left: 0, top: 0 };
let storedTooltipContent = '';
const closeTooltip = () => {
  const tooltipEl = document.getElementById('uptime-chart-tooltip');
  if (tooltipEl) {
    tooltipEl.style.opacity = '0';
    tooltipEl.style.display = 'none';
  }
  tooltipPinned.value = false;
  showDownloadButton.value = false;
  storedTooltipContent = '';
};

const downloadOfflineDevices = async () => {
  if (!chart) return;
  
  // Get the current tooltip date from the stored content
  const tooltipContentEl = document.getElementById('uptime-chart-tooltip-content');
  if (!tooltipContentEl) return;
  
  const dateMatch = tooltipContentEl.innerHTML.match(/class="tooltip-date">([^<]+)</);
  if (!dateMatch) return;
  
  const dateStr = new Date(dateMatch[1]).toISOString().split('T')[0];
  
  try {
    // Show loading state
    const downloadBtn = document.querySelector('.tooltip-download') as HTMLButtonElement;
    if (downloadBtn) {
      downloadBtn.disabled = true;
      downloadBtn.style.opacity = '0.5';
    }
    
    // Call the backend API using the proper API helper
    const { get } = useApi();
    const { responseBody } = await get('api/reports/offline-devices', {
      params: {
        date: dateStr
      },
      isLogoutOnInvalidAuth: true
    });
    
    const data = responseBody;
    
    if (!data.offlineDevices || data.offlineDevices.length === 0) {
      alert('No offline devices for this date');
      return;
    }
    
    // Create CSV content with all fields (excluding date column)
    const csvContent = [
      'Player ID,Player Name,Location,Last Connected,Description',
      ...data.offlineDevices.map((device: any) => {
        // Parse .NET JSON date format: /Date(1749773450463)/
        let lastConnected = 'Unknown';
        if (device.lastConnected) {
          const dateMatch = device.lastConnected.match(/\/Date\((\d+)\)\//);
          if (dateMatch) {
            const timestamp = parseInt(dateMatch[1]);
            lastConnected = new Date(timestamp).toLocaleString();
          }
        }
        
        return [
          device.playerId,
          `"${device.playerName || 'Unknown'}"`,
          `"${device.location || 'Unknown'}"`,
          `"${lastConnected}"`,
          `"${device.description || 'No description'}"`
        ].join(',');
      })
    ].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `offline-devices-${dateStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Error downloading offline devices:', error);
    alert('Failed to download offline devices report. Please try again.');
  } finally {
    // Reset loading state
    const downloadBtn = document.querySelector('.tooltip-download') as HTMLButtonElement;
    if (downloadBtn) {
      downloadBtn.disabled = false;
      downloadBtn.style.opacity = '1';
    }
  }
};

const processData = () => {
  console.log('UptimeChart: Processing data with', props.uptimeData?.length, 'records');
  console.log('UptimeChart: Start date:', props.startDate);
  console.log('UptimeChart: End date:', props.endDate);
  
  if (!props.uptimeData || props.uptimeData.length === 0) {
    console.log('UptimeChart: No data to process');
    return {
      labels: [],
      datasets: []
    };
  }

  // Create date range for display (user-selected dates)
  const displayDates: Date[] = [];
  const currentDate = new Date(props.startDate);
  const endDate = new Date(props.endDate);
  
  while (currentDate <= endDate) {
    displayDates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Also get the extra day for context (if present)
  const extraDay = new Date(props.endDate);
  extraDay.setDate(extraDay.getDate() + 1);
  const extraDayStr = extraDay.toISOString().split('T')[0];

  // Initialize data structure to track unique active devices per day
  const dailyData: { [date: string]: Set<number> } = {};
  
  // Initialize for display dates only
  displayDates.forEach(date => {
    const dateStr = date.toISOString().split('T')[0];
    dailyData[dateStr] = new Set();
  });

  // Get all unique devices from the data
  const allDevices = new Set<number>();
  props.uptimeData.forEach(record => {
    allDevices.add(record.playerId);
  });
  const totalDevices = allDevices.size;

  // Process uptime data to count unique devices
  props.uptimeData.forEach(record => {
    const startDate = new Date(record.dateStarted);
    const endDate = new Date(record.dateEnded);
    
    // Iterate through each day in the range
    const currentDate = new Date(startDate);
    while (currentDate <= endDate && currentDate <= extraDay) {
      const dateStr = currentDate.toISOString().split('T')[0];
      
      if (dailyData[dateStr]) {
        // If this device had activity on this day (dateStarted/dateEnded overlap with this day),
        // consider it "active" for that day
        dailyData[dateStr].add(record.playerId); 
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
  });

  // Convert to chart format - count online and offline devices
  const labels = displayDates.map(date => date.toLocaleDateString());
  const onlineData = displayDates.map(date => {
    const dateStr = date.toISOString().split('T')[0];
    return dailyData[dateStr]?.size || 0; // Devices with activity = online
  });
  const offlineData = displayDates.map((date, idx) => {
    const dateStr = date.toISOString().split('T')[0];
    const onlineCount = dailyData[dateStr]?.size || 0;
    // For the last day, only count as offline if not online on the extra day
    if (idx === displayDates.length - 1) {
      // Devices online on extra day
      const onlineOnExtraDay = dailyData[extraDayStr] || new Set();
      let trulyOffline = 0;
      allDevices.forEach(id => {
        if (!onlineOnExtraDay.has(id) && !(dailyData[dateStr] && dailyData[dateStr].has(id))) {
          trulyOffline++;
        }
      });
      // Devices that were online on last day but not on extra day are not counted as offline
      // Devices that are not online on last day and not on extra day are counted as offline
      return trulyOffline;
    }
    return totalDevices - onlineCount; // Total devices - online devices = offline devices
  });

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Online',
        data: onlineData,
        backgroundColor: '#BAFF5C', // BUI lemonlime (--bui-cta-pri-fill)
        borderColor: '#82BF2E',
        borderWidth: 0,
        stack: 'stack0'
      },
      {
        label: 'Offline',
        data: offlineData,
        backgroundColor: '#DE3DA9', // BUI orchid (--bui-badge-bad-fill)
        borderColor: '#B02A86',
        borderWidth: 0,
        stack: 'stack0'
      }
    ]
  };
  
  // Build a map of playerId to playerName for quick lookup
  const playerIdToName: Record<number, string> = {};
  props.uptimeData.forEach(record => {
    playerIdToName[record.playerId] = record.playerName;
  });
  // For each day, also track which playerIds are offline
  const offlinePlayerIdsByDate: Record<string, number[]> = {};
  displayDates.forEach((date, idx) => {
    const dateStr = date.toISOString().split('T')[0];
    let offlineIds: number[] = [];
    if (idx === displayDates.length - 1) {
      // Last day: only offline if not online on extra day
      const onlineOnExtraDay = dailyData[extraDayStr] || new Set();
      allDevices.forEach(id => {
        if (!onlineOnExtraDay.has(id) && !(dailyData[dateStr] && dailyData[dateStr].has(id))) {
          offlineIds.push(id);
        }
      });
    } else {
      allDevices.forEach(id => {
        if (!dailyData[dateStr] || !dailyData[dateStr].has(id)) {
          offlineIds.push(id);
        }
      });
    }
    offlinePlayerIdsByDate[dateStr] = offlineIds;
  });
  // Attach to chartData for use in tooltip
  (chartData as any)._offlinePlayerIdsByDate = offlinePlayerIdsByDate;
  (chartData as any)._playerIdToName = playerIdToName;
  console.log('UptimeChart: Processed chart data:', chartData);
  return chartData;
};

// Build chart options with colors resolved from the BUI theme tokens on the
// canvas (theme-aware): legend/title use the card's text token, axes/grid use
// the muted detail + line tokens — replacing Chart.js's default grey.
const buildChartOptions = (el: HTMLElement): ChartOptions<'bar'> => {
  const cs = getComputedStyle(el);
  const tok = (name: string, fallback: string) => cs.getPropertyValue(name).trim() || fallback;
  const textColor = tok('--bui-tbl-row-text', '#2a2969');
  const detailColor = tok('--bui-tbl-detail-text', '#5c5c66');
  const gridColor = tok('--bui-tbl-line', '#ffd9e6');
  return {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
      labels: { color: textColor },
    },
    title: {
      display: true,
      text: 'Devices Uptime Status by Day',
      color: textColor,
    },
    tooltip: {
      enabled: false, // Disable the default tooltip
      external: function() {
        // Only show tooltip when pinned (clicked), not on hover
        if (!tooltipPinned.value) {
          const tooltipEl = document.getElementById('uptime-chart-tooltip');
          if (tooltipEl) {
            tooltipEl.style.display = 'none';
          }
          return;
        }
        
        let tooltipEl = document.getElementById('uptime-chart-tooltip');
        let tooltipContentEl = document.getElementById('uptime-chart-tooltip-content');
        if (!tooltipEl || !tooltipContentEl) return;
        // Set position fixed in viewport
        tooltipEl.style.display = 'block';
        tooltipEl.style.opacity = '1';
        tooltipEl.style.position = 'fixed';
        const canvas = chartCanvas.value;
        if (canvas) {
          const container = canvas.parentElement;
          if (container) {
            const containerRect = container.getBoundingClientRect();
            tooltipEl.style.left = (containerRect.left + tooltipPosition.left) + 'px';
            tooltipEl.style.top = (containerRect.top + tooltipPosition.top) + 'px';
          }
        }
        // Always show the stored tooltip content, ignore hover events
        tooltipContentEl.innerHTML = storedTooltipContent;
      }
    }
  },
  scales: {
    x: {
      stacked: true,
      title: {
        display: true,
        text: 'Date',
        color: detailColor,
      },
      ticks: { color: detailColor },
      grid: { color: gridColor },
    },
    y: {
      stacked: true,
      title: {
        display: true,
        text: 'Number of Devices',
        color: detailColor,
      },
      ticks: {
        color: detailColor,
        callback: function(value: number | string) {
          return `${value}`;
        }
      },
      grid: { color: gridColor },
    }
  }
  };
};

const createChart = () => {
  console.log('UptimeChart: Creating chart');
  if (!chartCanvas.value) {
    console.log('UptimeChart: No canvas element found');
    return;
  }

  const chartData = processData();
  console.log('UptimeChart: Creating chart with data:', chartData);
  
  try {
    chart = new ChartJS(chartCanvas.value, {
      type: 'bar',
      data: chartData,
      options: buildChartOptions(chartCanvas.value)
    });
    console.log('UptimeChart: Chart created successfully');
  } catch (error) {
    console.error('UptimeChart: Error creating chart:', error);
  }
};

const updateChart = () => {
  if (chart) {
    const chartData = processData();
    chart.data = chartData;
    chart.update();
  }
};

function handleCanvasClick(event: MouseEvent) {
  if (!chart) return;
  const points = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
  if (points.length > 0) {
    const firstPoint = points[0];
    const datasetIndex = firstPoint.datasetIndex;
    const dataIndex = firstPoint.index;
    // Show tooltip for both online and offline bars
    tooltipPinned.value = true;
    
    // Generate and store the tooltip content for this clicked bar
    const chartData = chart?.data as any;
    const dateLabel = chartData.labels[dataIndex];
    let dateHtml = `<div class="tooltip-date">${dateLabel}</div>`;
    const datasetLabel = chartData.datasets[datasetIndex].label;
    
    if (datasetLabel === 'Offline' && chartData._offlinePlayerIdsByDate && chartData._playerIdToName) {
      const dateStr = typeof dateLabel === 'string' ? new Date(dateLabel).toISOString().split('T')[0] : '';
      const offlineIds = chartData._offlinePlayerIdsByDate[dateStr] || [];
      showDownloadButton.value = offlineIds.length > 0; // Show download button only if there are offline devices
      if (offlineIds.length === 0) {
        storedTooltipContent = dateHtml + '<div class="tooltip-title">Offline: 0 devices</div>';
      } else {
        storedTooltipContent = dateHtml + `<div class="tooltip-title">Offline: ${offlineIds.length} devices</div>`;
        storedTooltipContent += '<div class="tooltip-list">';
        offlineIds.forEach((id: any) => {
          const name = chartData._playerIdToName[id] || `Player ${id}`;
          storedTooltipContent += `<div><a class='tooltip-link' href='/players/edit/${id}' target='_blank'>${name}</a></div>`;
        });
        storedTooltipContent += '</div>';
      }
    } else if (datasetLabel === 'Online') {
      showDownloadButton.value = false; // Hide download button for online devices
      const onlineCount = chartData.datasets[datasetIndex].data[dataIndex];
      storedTooltipContent = dateHtml + `<div class='tooltip-title'>Online: ${onlineCount} devices</div>`;
    } else {
      showDownloadButton.value = false; // Hide download button for other datasets
      const count = chartData.datasets[datasetIndex].data[dataIndex];
      storedTooltipContent = dateHtml + `<div class='tooltip-title'>${datasetLabel}: ${count} devices</div>`;
    }
    
    // Position tooltip at mouse click relative to the chart container
    const canvas = chartCanvas.value;
    if (canvas) {
      const container = canvas.parentElement;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        tooltipPosition.left = event.clientX - containerRect.left + 5; // Relative to container
        tooltipPosition.top = event.clientY - containerRect.top + 5; // Relative to container
      }
    }
    // Manually trigger chart update to show tooltip
    chart.setActiveElements([{ datasetIndex, index: dataIndex }]);
    if (chart.tooltip) {
      chart.tooltip.setActiveElements([{ datasetIndex, index: dataIndex }], { x: 0, y: 0 });
    }
    chart.update();
  }
}

onMounted(() => {
  createChart();
  nextTick(() => {
    // Chart is now created and ready
  });
  
  // Add window resize listener to close tooltip
  const handleResize = () => {
    closeTooltip();
  };
  window.addEventListener('resize', handleResize);
  
  // Add scroll listeners to update tooltip position
  const handleScroll = () => {
    if (tooltipPinned.value) {
      const tooltipEl = document.getElementById('uptime-chart-tooltip');
      const canvas = chartCanvas.value;
      if (tooltipEl && canvas) {
        const container = canvas.parentElement;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          // Update tooltip position to stay anchored to the chart
          tooltipEl.style.left = (containerRect.left + tooltipPosition.left) + 'px';
          tooltipEl.style.top = (containerRect.top + tooltipPosition.top) + 'px';
        }
      }
    }
  };
  
  window.addEventListener('scroll', handleScroll, true); // true for capture phase
  document.addEventListener('scroll', handleScroll, true);
  
  // Store the handlers for cleanup
  (window as any)._uptimeChartResizeHandler = handleResize;
  (window as any)._uptimeChartScrollHandler = handleScroll;
});

onUnmounted(() => {
  const canvas = chartCanvas.value;
  const tooltipEl = document.getElementById('uptime-chart-tooltip');
  if (canvas) {
    canvas.onmousemove = null;
    canvas.onmouseleave = null;
  }
  if (tooltipEl) {
    tooltipEl.onmouseenter = null;
    tooltipEl.onmouseleave = null;
  }
  if (chart) {
    chart.destroy();
  }
  closeTooltip();
  
  // Remove resize listener
  const resizeHandler = (window as any)._uptimeChartResizeHandler;
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler);
    delete (window as any)._uptimeChartResizeHandler;
  }

  // Remove scroll listeners
  const scrollHandler = (window as any)._uptimeChartScrollHandler;
  if (scrollHandler) {
    window.removeEventListener('scroll', scrollHandler, true);
    document.removeEventListener('scroll', scrollHandler, true);
    delete (window as any)._uptimeChartScrollHandler;
  }
});

watch(() => [props.uptimeData, props.startDate, props.endDate], () => {
  closeTooltip();
  updateChart();
}, { deep: true });
</script>

<style scoped>
.uptime-chart-container {
  width: 100%;
  height: 400px;
  position: relative;
}
.uptime-chart-tooltip {
  min-width: 250px;
  max-width: 250px;
  max-height: 240px;
  background: var(--bui-tbl-row-fill, #fffcfd);
  color: var(--bui-tbl-row-text, #2a2969);
  border: 1px solid var(--bui-tbl-line, #ffd9e6);
  border-radius: 8px;
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 32px rgba(75, 24, 134, 0.16));
  padding: 0;
  font-size: 0.95rem;
  z-index: 99999;
  pointer-events: auto;
  transition: opacity 0.15s;
  position: relative;
  overflow: hidden;
}
.tooltip-download {
  position: absolute;
  top: 4px;
  right: 30px;
  background: transparent;
  padding: 2px 4px;
  border: none;
  font-size: 1.2em;
  color: var(--bui-tbl-detail-text, #5c5c66);
  cursor: pointer;
  z-index: 2;
  line-height: 1;
  border-radius: 4px;
  transition: background-color 0.2s;
}
.tooltip-download:hover {
  background: var(--bui-badge-tag-fill, #ffd9e6);
  color: var(--bui-tbl-row-text, #2a2969);
}
.tooltip-close {
  position: absolute;
  top: 4px;
  right: 12px;
  background: transparent;
  padding: 2px 4px;
  border: none;
  font-size: 1.2em;
  color: var(--bui-tbl-detail-text, #5c5c66);
  cursor: pointer;
  z-index: 2;
  line-height: 1;
  border-radius: 4px;
  transition: background-color 0.2s;
}
.tooltip-close:hover {
  background: var(--bui-badge-tag-fill, #ffd9e6);
  color: var(--bui-tbl-row-text, #2a2969);
}
#uptime-chart-tooltip-content {
  padding: 12px 16px 16px 16px;
  padding-top: 16px;
  max-height: 200px;
  overflow-y: auto;
  margin-right: 4px;
}

/* Custom scrollbar styling for the tooltip content */
#uptime-chart-tooltip-content::-webkit-scrollbar {
  width: 4px;
}

#uptime-chart-tooltip-content::-webkit-scrollbar-track {
  background: transparent;
  margin: 4px 0;
}

#uptime-chart-tooltip-content::-webkit-scrollbar-thumb {
  background: var(--bui-tbl-line, #ffd9e6);
  border-radius: 2px;
}

#uptime-chart-tooltip-content::-webkit-scrollbar-thumb:hover {
  background: var(--bui-badge-tag-text, #a420c8);
}
.uptime-chart-tooltip .tooltip-title {
  font-weight: 600;
  margin-bottom: 0.5em;
  margin-top: 0.5em;
}
.uptime-chart-tooltip .tooltip-list {
  display: flex;
  flex-direction: column;
  gap: 0.25em;
}
.uptime-chart-tooltip .tooltip-link {
  color: var(--bui-tbl-row-link, #4b1886);
  text-decoration: underline;
  cursor: pointer;
  pointer-events: auto;
}
.uptime-chart-tooltip .tooltip-more {
  color: var(--bui-tbl-detail-text, #5c5c66);
  font-size: 0.9em;
  margin-top: 0.5em;
}
.tooltip-date {
  font-weight: 700;
  margin-bottom: 0.25em;
  color: var(--bui-tbl-row-text, #2a2969);
}
</style> 